// src/jornada/iniciarJornada.jsx
import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { jornadaStore } from "../data/jornadaStore";

export const IniciarJornada = ({ onJornadaActualizada, onSuccess, onError, uiLocked = false }) => {
  const [centros, setCentros] = useState([]);
  const [centro, setCentro] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [coordenadas, setCoordenadas] = useState({ latitud: null, longitud: null });
  const [inicio, setInicio] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const limpiarMensajeTimeout = useRef(null);

  const flash = (msg, ms = 4500) => {
    setMensaje(msg);
    if (limpiarMensajeTimeout.current) clearTimeout(limpiarMensajeTimeout.current);
    limpiarMensajeTimeout.current = setTimeout(() => setMensaje(""), ms);
  };

  // Carga “centros” desde la store (usa caché si offline / precalentado en bootstrap)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await jornadaStore.getCentros(); // offline-first
        if (alive) setCentros(Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn("[IniciarJornada] getCentros:", e?.message);
        if (alive) setCentros([]);
      }
    })();
    return () => {
      alive = false;
      if (limpiarMensajeTimeout.current) clearTimeout(limpiarMensajeTimeout.current);
    };
  }, []);

  const calcularDistanciaKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // km
  };

  const capturarUbicacionYCrearJornada = () => {
    if (!centro) return flash("Selecciona un centro.");
    if (isSubmitting || uiLocked) return;

    // Evita duplicar inicio si ya hay activa (local o servidor)
    const snap = jornadaStore.snapshot ? jornadaStore.snapshot() : { active: null, local: null };
    const active = snap.active;
    const local  = snap.local;

    const activeOpen =
      !!active && !active?.pendingFinish && !active?.pendingCancel;
    const localPending =
      !!local && (local.pendingFinish || local.pendingCancel);

    if (activeOpen) {
      onError?.("Ya tienes un fichaje en curso. Ve a 'Finalizar' para cerrarlo.");
      onJornadaActualizada?.();
      return;
    }

    if (localPending) {
      onError?.(
        "Tienes una jornada pendiente de envío. Espera a que se sincronice antes de iniciar otra."
      );
      onJornadaActualizada?.();
      return;
    }

    const centroSel = centros.find((c) => c.id === Number(centro));
    if (!centroSel?.latitud || !centroSel?.longitud) {
      flash("Este centro no tiene coordenadas asignadas.");
      return;
    }

    if (!navigator.geolocation) {
      onError?.("Tu navegador no soporta geolocalización.");
      return;
    }

    setIsSubmitting(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const latitud = position.coords.latitude;
          const longitud = position.coords.longitude;

          const distanciaKm = calcularDistanciaKm(
            latitud, longitud,
            parseFloat(centroSel.latitud), parseFloat(centroSel.longitud)
          );

          if (distanciaKm > 1) {
            const confirmar = window.confirm(
              `Estás a ${distanciaKm.toFixed(2)} km del centro. ¿Deseas iniciar igualmente?`
            );
            if (!confirmar) {
              flash("Registro cancelado por el usuario.");
              setIsSubmitting(false);
              return;
            }
          }

          // fingerprint para el backend
          const fp = await FingerprintJS.load();
          const { visitorId: fingerprint } = await fp.get();

          // ✅ delega en la store: ella decide online vs offline y gestiona outbox
          const result = await jornadaStore.start({
            centroId: Number(centro),
            centroNombre: centroSel?.nombre || "",
            latitud,
            longitud,
            fingerprint,
          });

          // Puede ocurrir si la store detecta una activa/pending que no teníamos en el snapshot
          if (result?.status === "noop") {
            onError?.("Ya tienes una jornada en curso o pendiente de envío.");
            await onJornadaActualizada?.();
            setIsSubmitting(false);
            return;
          }

          setInicio(new Date());
          setCoordenadas({ latitud, longitud });

          if (result?.status === "online") {
            onSuccess?.("Jornada iniciada correctamente.");
          } else if (result?.status === "offline") {
            onSuccess?.("Sin conexión: fichaje guardado y se enviará automáticamente.");
          } else {
            // fallback genérico
            onSuccess?.("Jornada registrada.");
          }

          await onJornadaActualizada?.(); // JornadaPage decidirá cambiar a “Finalizar”
        } catch (e) {
          console.error(e);
          onError?.("No se pudo iniciar la jornada.");
        } finally {
          setIsSubmitting(false);
        }
      },
      (err) => {
        console.error(err);
        onError?.("No se pudo obtener la ubicación.");
        setIsSubmitting(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const cancelarFormulario = () => {
    setCentro("");
    setMensaje("");
    setCoordenadas({ latitud: null, longitud: null });
    setInicio(null);
  };

  const centroOptions = (centros || [])
    .map((c) => ({ value: c.id, label: c.nombre }))
    .sort((a, b) => (a.label || "").localeCompare(b.label || ""));

  const valueFrom = (options, value) =>
    options.find((opt) => String(opt.value) === String(value)) || null;

  const botonDeshabilitado =
    uiLocked || isSubmitting || !centro || !Array.isArray(centros) || centros.length === 0;

  return (
    <div className="formulario-ticaje">
      <h2 className="centrar-titulo">Iniciar Jornada Laboral</h2>
      {mensaje && <p className="rojo">{mensaje}</p>}

      {/* Aviso útil si no hay centros cacheados y estás offline */}
      {(!centros?.length) && !navigator.onLine && (
        <p className="rojo" style={{ marginTop: 8 }}>
          No hay centros en caché. Abre la app con conexión al menos una vez para precargarlos.
        </p>
      )}

      <form onSubmit={(e) => e.preventDefault()} className="form-agregar-tag registro-fecha">
        <div className="form-group">
          <label>Centro:</label>
          <Select
            options={centroOptions}
            value={valueFrom(centroOptions, centro)}
            onChange={(opt) => setCentro(opt?.value || "")}
            isClearable
            isDisabled={!centros || centros.length === 0 || isSubmitting || uiLocked}
            className="select-centro rs-full"
            classNamePrefix="select"
            placeholder="Selecciona un centro"
            noOptionsMessage={() => "No hay centros disponibles"}
            aria-label="Seleccionar centro"
            inputId="select-centro"
          />
        </div>

        <button
          type="button"
          className="btn-login"
          onClick={capturarUbicacionYCrearJornada}
          disabled={botonDeshabilitado}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? "Iniciando..." : "Capturar Ubicación e Iniciar"}
        </button>

        <button
          type="button"
          className="btn-registrate"
          onClick={cancelarFormulario}
          disabled={isSubmitting || uiLocked}
          style={{ marginTop: 10 }}
        >
          Limpiar
        </button>

        {inicio && (
          <p style={{ fontSize: 12, marginTop: 10 }}>
            Inicio local: {new Date(inicio).toLocaleString()}
            {coordenadas.latitud && (
              <> — ({coordenadas.latitud.toFixed(5)}, {coordenadas.longitud?.toFixed(5)})</>
            )}
          </p>
        )}
      </form>
    </div>
  );
};
